/* profiles-builtin.js — Profils prédéfinis (JSON). statut : 'maintenu'
 * (contraintes techniques stables) ou 'gabarit' (à adapter). Expose self.MediaProfiles. */
'use strict';

var APPLE_SPECS = 'https://www.apple.com/fr/';
var CONSULTED = '2026-09-18';
var TODAY = '2026-09-20'; // sources consultées lors de l'ajout des profils de streaming


/* Débits conseillés par YouTube, en Mb/s, par définition d'image : cadence
 * courante (24, 25, 30) puis cadence élevée (48, 50, 60), en SDR puis en HDR.
 * Les fourchettes publiées (35 à 45 Mb/s en 2160p) sont ramenées à leur
 * plancher : au-dessous, l'image souffrira du réencodage de la plateforme.
 * Chaque définition forme une tranche ; la dernière n'a pas de plafond. */
var YT_BITRATES = [
  { h: 2160, sdr: [35, 53], hdr: [44, 66], label: '2160p (4K)' },
  { h: 1440, sdr: [16, 24], hdr: [20, 30], label: '1440p' },
  { h: 1080, sdr: [8, 12],  hdr: [10, 15], label: '1080p' },
  { h: 720,  sdr: [5, 7.5], hdr: [6.5, 9.5], label: '720p' },
  { h: 480,  sdr: [2.5, 4], hdr: null,     label: '480p' },
  { h: 360,  sdr: [1, 1.5], hdr: null,     label: '360p' }
];

/* Tranche de définition d'une image : une marge de 8 % rattache à sa classe
 * une image rognée par des bandes noires, par exemple un film en 1920 × 1024,
 * qui est bien un téléversement 1080p et non 720p. */
function ytRange(i) {
  var min = Math.round(YT_BITRATES[i].h * 0.92);
  var max = i === 0 ? 100000 : Math.round(YT_BITRATES[i - 1].h * 0.92) - 1;
  return { min: min, max: max };
}

// Une règle de débit par tranche de définition, par cadence et par plage dynamique.
function ytBitrateRules() {
  var rules = [];
  YT_BITRATES.forEach(function (b, i) {
    var range = ytRange(i);
    [['sdr', 'absent', 'SDR'], ['hdr', 'exists', 'HDR']].forEach(function (dyn) {
      var values = b[dyn[0]];
      if (!values) return;
      [['courante', 'lte', 30, values[0], '24, 25 ou 30 images par seconde'],
       ['elevee', 'gt', 30, values[1], 'cadence élevée (48, 50 ou 60 images par seconde)']].forEach(function (fps) {
        rules.push({
          id: 'debit-' + b.h + '-' + dyn[0] + '-' + fps[0],
          track: 'video', quantifier: 'all', field: 'bitRate', op: 'gte', value: fps[3] * 1000000,
          when: [
            { field: 'hdrFormat', op: dyn[1] },
            { field: 'height', op: 'range', value: range },
            { field: 'frameRate', op: fps[1], value: fps[2] }
          ],
          severity: 'warning',
          message: b.label + ' en ' + dyn[2] + ', ' + fps[4] + ' : débit conseillé d’au moins ' +
            String(fps[3]).replace('.', ',') + ' Mb/s.'
        });
      });
    });
  });
  return rules;
}

var PROFILES = [
  {
    id: 'ce-navigateur',
    family: 'lecture',
    notChecked: [
      'La lecture n’est pas réellement tentée : le navigateur est interrogé sur ce qu’il déclare savoir décoder.',
      'Les sous-titres, les chapitres et les pièces jointes ne sont pas testés.',
      'Le résultat vaut pour ce navigateur, sur cet appareil, aujourd’hui.'
    ],
    name: 'Ce navigateur, cet appareil (test réel)',
    version: '1.0.0', date: '2026-09', statut: 'maintenu',
    description: 'Pose la question au navigateur lui-même : il indique s’il sait décoder chaque piste vidéo et audio, et s’il reconnaît le conteneur. Seuls H.264, HEVC, VP9, AV1, AAC, Opus, MP3 et FLAC sont testables ; les autres pistes sont non évaluables.',
    sources: [
      { title: 'MDN — MediaCapabilities.decodingInfo()', url: 'https://developer.mozilla.org/en-US/docs/Web/API/MediaCapabilities/decodingInfo', consulted: CONSULTED },
      { title: 'MDN — HTMLMediaElement.canPlayType()', url: 'https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/canPlayType', consulted: CONSULTED }
    ],
    appliesTo: {},
    rules: [
      { id: 'container', track: 'container', field: 'browserContainer', origin: 'measured', op: 'eq', value: true,
        severity: 'error', message: 'Conteneur reconnu par ce navigateur.' },
      { id: 'video', track: 'video', quantifier: 'all', field: 'browserDecode', origin: 'measured', op: 'eq', value: true,
        severity: 'error', message: 'Vidéo décodable par ce navigateur.' },
      { id: 'video-smooth', track: 'video', quantifier: 'all', field: 'browserSmooth', origin: 'measured', op: 'eq', value: true,
        severity: 'warning', message: 'Lecture vidéo annoncée fluide par ce navigateur.', when: { field: 'browserDecode', origin: 'measured', op: 'eq', value: true } },
      { id: 'audio', track: 'audio', quantifier: 'all', field: 'browserDecode', origin: 'measured', op: 'eq', value: true,
        severity: 'error', message: 'Audio décodable par ce navigateur.' }
    ]
  },
  {
    id: 'apple-iphone-ipad',
    family: 'lecture',
    notChecked: [
      'Le débit : Apple ne publie pas de plafond par appareil.',
      'Les formats de sous-titres, qu’Apple ne liste pas pour la lecture de fichiers locaux.'
    ],
    name: 'Famille Apple — iPhone et iPad',
    version: '1.0.0', date: '2026-09', statut: 'maintenu',
    description: 'Formats de lecture publiés par Apple pour les iPhone et iPad vendus en septembre 2026 (iPhone 18 Pro, 17, Air, 17e ; iPad 11 pouces A16, Air, Pro, mini). Là où les modèles divergent, la règle est un avertissement qui nomme les modèles concernés. Les sources ne disent rien du conteneur : il n’est pas contrôlé.',
    sources: [
      { title: 'Apple — iPhone 18 Pro et 18 Pro Max, caractéristiques', url: APPLE_SPECS + 'iphone-18-pro/specs/', consulted: CONSULTED },
      { title: 'Apple — iPhone 17, caractéristiques', url: APPLE_SPECS + 'iphone-17/specs/', consulted: CONSULTED },
      { title: 'Apple — iPhone Air, caractéristiques', url: APPLE_SPECS + 'iphone-air/specs/', consulted: CONSULTED },
      { title: 'Apple — iPhone 17e, caractéristiques', url: APPLE_SPECS + 'iphone-17e/specs/', consulted: CONSULTED },
      { title: 'Apple — iPad 11 pouces (A16), caractéristiques', url: APPLE_SPECS + 'ipad-11/specs/', consulted: CONSULTED },
      { title: 'Apple — iPad Air, caractéristiques', url: APPLE_SPECS + 'ipad-air/specs/', consulted: CONSULTED },
      { title: 'Apple — iPad Pro, caractéristiques', url: APPLE_SPECS + 'ipad-pro/specs/', consulted: CONSULTED },
      { title: 'Apple — iPad mini, caractéristiques', url: APPLE_SPECS + 'ipad-mini/specs/', consulted: CONSULTED }
    ],
    appliesTo: {},
    rules: [
      { id: 'vcodec', track: 'video', quantifier: 'all', field: 'formatRaw', op: 'inList', value: ['AVC', 'HEVC', 'AV1', 'ProRes'],
        severity: 'error', message: 'Codec vidéo listé par Apple : H.264, HEVC, AV1 (ProRes sur certains modèles).' },
      { id: 'av1-ipad', track: 'video', quantifier: 'all', field: 'formatRaw', op: 'ne', value: 'AV1',
        severity: 'warning', message: 'AV1 : non listé pour l’iPad 11 pouces (A16).' },
      { id: 'prores', track: 'video', quantifier: 'all', field: 'formatRaw', op: 'ne', value: 'ProRes',
        severity: 'warning', message: 'ProRes : listé seulement pour l’iPhone 18 Pro, l’iPad Air, l’iPad Pro et l’iPad mini.' },
      { id: 'hdr10plus-ipad', track: 'video', quantifier: 'all', field: 'hdrFormat', op: 'notContains', value: '2094', when: { field: 'hdrFormat', op: 'exists' },
        severity: 'warning', message: 'HDR10+ : non listé pour l’iPad 11 pouces (A16), qui annonce Dolby Vision et HDR10.' },
      { id: 'hlg-ipad', track: 'video', quantifier: 'all', field: 'transferCharacteristics', op: 'notContains', value: 'HLG', when: { field: 'transferCharacteristics', op: 'exists' },
        severity: 'warning', message: 'HLG : non listé pour l’iPad 11 pouces (A16), qui annonce Dolby Vision et HDR10.' },
      { id: 'acodec', track: 'audio', quantifier: 'all', field: 'formatRaw', op: 'inList', value: ['AAC', 'MPEG Audio', 'ALAC', 'FLAC', 'AC-3', 'E-AC-3'],
        severity: 'error', message: 'Format audio listé par Apple : AAC, MP3, Apple Lossless, FLAC, Dolby Digital, Dolby Digital Plus.' }
    ]
  },
  {
    id: 'pret-pour-le-web',
    family: 'diffusion',
    notChecked: [
      'La cadence d’images : les valeurs admises sont listées par le profil « Diffusion en streaming ».',
      'Le poids et la durée du fichier, qui dépendent de la page où il est posé.',
      'Le niveau sonore, qui demande une mesure.'
    ],
    name: 'Prêt pour le web',
    version: '1.0.0', date: '2026-09', statut: 'maintenu',
    description: 'Combinaisons recommandées par MDN pour une vidéo de site web : WebM avec AV1 et Opus, ou MP4 avec H.264 et AAC. Pour un MP4, un index (moov) placé avant les données permet de démarrer la lecture sans attendre la fin du téléchargement ; c’est un avertissement, les navigateurs sachant souvent aller chercher un index placé à la fin. La cadence n’est pas contrôlée ici : les valeurs admises sont listées par la spécification HLS d’Apple, et servent au profil « Diffusion en streaming ».',
    sources: [
      { title: 'MDN — Web video codec guide, « Recommendations for the web » (modifiée le 7 septembre 2026)', url: 'https://developer.mozilla.org/en-US/docs/Web/Media/Guides/Formats/Video_codecs', consulted: CONSULTED },
      { title: 'MDN — Media container formats (modifiée le 10 juin 2025)', url: 'https://developer.mozilla.org/en-US/docs/Web/Media/Guides/Formats/Containers', consulted: CONSULTED },
      { title: 'FFmpeg — Formats Documentation, option movflags faststart', url: 'https://ffmpeg.org/ffmpeg-formats.html', consulted: CONSULTED }
    ],
    appliesTo: { requireTracks: { video: 1 } },
    rules: [
      { id: 'container', track: 'container', field: 'formatRaw', op: 'inList', value: ['MPEG-4', 'WebM'],
        severity: 'error', message: 'Conteneur MP4 ou WebM.' },
      { id: 'vcodec', track: 'video', quantifier: 'all', field: 'formatRaw', op: 'inList', value: ['AVC', 'AV1'],
        severity: 'error', message: 'Vidéo H.264 (dans un MP4) ou AV1 (dans un WebM).' },
      { id: 'acodec', track: 'audio', quantifier: 'all', field: 'formatRaw', op: 'inList', value: ['AAC', 'Opus'],
        severity: 'error', message: 'Audio AAC (dans un MP4) ou Opus (dans un WebM).' },
      { id: 'moov', track: 'container', field: 'moovPosition', origin: 'measured', op: 'eq', value: 'before-mdat',
        when: { field: 'formatRaw', op: 'eq', value: 'MPEG-4' },
        severity: 'warning', message: 'MP4 : index (moov) placé avant les données, pour démarrer sans attendre la fin du téléchargement.' }
    ]
  },
  {
    id: 'streaming-hls',
    family: 'diffusion',
    notChecked: [
      'Le MP4 fragmenté exigé par les règles 1.2 et 1.5 : le modèle ne distingue pas un MP4 fragmenté d’un MP4 ordinaire.',
      'L’image clé toutes les deux secondes (règle 1.13), qui demande de mesurer les paquets.'
    ],
    name: 'Diffusion en streaming (HLS, Apple)',
    version: '1.0.0', date: '2026-09', statut: 'maintenu',
    description: 'Exigences de la spécification d’édition HLS d’Apple pour la vidéo à la demande, numérotées dans le document : codecs (1.1), désentrelacement (1.14), cadences admises (1.18), plafond de 60 images par seconde (1.19), 30 au plus en HDR (1.20), jeux de paramètres dans les descriptions d’échantillon (1.10). Deux exigences ne sont pas contrôlées, faute de pouvoir les lire dans le fichier : le MP4 fragmenté (1.2 et 1.5) et l’image clé toutes les deux secondes (1.13), qui demande de mesurer les paquets.',
    sources: [
      { title: 'Apple — HTTP Live Streaming (HLS) authoring specification for Apple devices', url: 'https://developer.apple.com/documentation/http-live-streaming/hls-authoring-specification-for-apple-devices', consulted: TODAY }
    ],
    appliesTo: { requireTracks: { video: 1 } },
    rules: [
      { id: 'vcodec', track: 'video', quantifier: 'all', field: 'formatRaw', op: 'inList', value: ['AVC', 'HEVC', 'AV1'],
        severity: 'error', message: 'Vidéo encodée en H.264, HEVC, Dolby Vision ou AV1 (règle 1.1).' },
      { id: 'progressif', track: 'video', quantifier: 'all', field: 'scanType', op: 'eq', value: 'progressive',
        severity: 'error', message: 'Tout contenu entrelacé doit être désentrelacé (règle 1.14).' },
      { id: 'fps-max', track: 'video', quantifier: 'all', field: 'frameRate', op: 'lte', value: 60,
        severity: 'error', message: 'Pas de cadence au-dessus de 60 images par seconde (règle 1.19).' },
      { id: 'fps-liste', track: 'video', quantifier: 'all', field: 'frameRate', op: 'inList',
        value: [23.976, 24, 25, 29.97, 30, 50, 59.94, 60],
        severity: 'warning', message: 'Cadence parmi celles prises en charge : 23,976, 24, 25, 29,97, 30, 50, 59,94 ou 60 images par seconde (règle 1.18).' },
      { id: 'fps-hdr', track: 'video', quantifier: 'all', field: 'frameRate', op: 'lte', value: 30,
        when: { field: 'hdrFormat', op: 'exists' },
        severity: 'warning', message: 'En HDR, cadence de 30 images par seconde au plus (règle 1.20).' },
      { id: 'avc3', track: 'video', quantifier: 'all', field: 'codecId', op: 'notContains', value: 'avc3',
        severity: 'warning', message: 'H.264 : jeux de paramètres dans les descriptions d’échantillon (avc1 plutôt qu’avc3, règle 1.10).' },
      { id: 'hev1', track: 'video', quantifier: 'all', field: 'codecId', op: 'notContains', value: 'hev1',
        severity: 'warning', message: 'HEVC : jeux de paramètres dans les descriptions d’échantillon (hvc1 plutôt que hev1, règle 1.10).' }
    ]
  },
  {
    id: 'youtube-televersement',
    name: 'YouTube — téléversement',
    family: 'diffusion',
    version: '1.0.0', date: '2026-09', statut: 'maintenu',
    description: 'Réglages d’encodage recommandés par YouTube. Presque tout y est une recommandation, pas une exigence : un fichier qui s’en écarte est accepté, mais réencodé plus lourdement. Deux exceptions signalées comme telles par YouTube : les listes de montage (edit lists) empêchent le traitement correct du fichier, et une vidéo HDR doit porter ses étiquettes de couleur pour être traitée.',
    notChecked: [
      'Les listes de montage (edit lists), que le modèle ne lit pas encore.',
      'Le niveau sonore : YouTube ne publie aucune cible.',
      'Les sous-titres, que YouTube attend en fichier séparé et non dans le fichier vidéo.'
    ],
    sources: [
      { title: 'YouTube — Recommended upload encoding settings (sans date affichée)', url: 'https://support.google.com/youtube/answer/1722171', consulted: TODAY },
      { title: 'YouTube — Upload high dynamic range (HDR) videos (sans date affichée)', url: 'https://support.google.com/youtube/answer/7126552', consulted: TODAY }
    ],
    appliesTo: { requireTracks: { video: 1 } },
    rules: [
      { id: 'conteneur', track: 'container', field: 'formatRaw', op: 'eq', value: 'MPEG-4',
        severity: 'warning', message: 'Conteneur MP4.' },
      { id: 'vcodec', track: 'video', quantifier: 'all', field: 'formatRaw', op: 'eq', value: 'AVC',
        severity: 'warning', message: 'Vidéo H.264.' },
      { id: 'profil', track: 'video', quantifier: 'all', field: 'formatProfile', op: 'contains', value: 'High',
        when: { field: 'formatRaw', op: 'eq', value: 'AVC' },
        severity: 'warning', message: 'H.264 en profil High.' },
      { id: 'progressif', track: 'video', quantifier: 'all', field: 'scanType', op: 'eq', value: 'progressive',
        severity: 'warning', message: 'Balayage progressif, sans entrelacement.' },
      { id: 'chroma', track: 'video', quantifier: 'all', field: 'chromaSubsampling', op: 'eq', value: '4:2:0',
        severity: 'warning', message: 'Sous-échantillonnage chroma 4:2:0.' },
      { id: 'moov', track: 'container', field: 'moovPosition', origin: 'measured', op: 'eq', value: 'before-mdat',
        when: { field: 'formatRaw', op: 'eq', value: 'MPEG-4' },
        severity: 'warning', message: 'Index (moov) en tête du fichier (« fast start »).' },
      { id: 'acodec', track: 'audio', quantifier: 'all', field: 'formatRaw', op: 'inList', value: ['AAC', 'Opus'],
        severity: 'warning', message: 'Audio AAC-LC ou Opus.' },
      { id: 'freq', track: 'audio', quantifier: 'all', field: 'samplingRate', op: 'eq', value: 48000,
        severity: 'warning', message: 'Fréquence d’échantillonnage de 48 kHz.' },
      { id: 'hdr-primaires', track: 'video', quantifier: 'all', field: 'colorPrimaries', op: 'contains', value: '2020',
        when: { field: 'hdrFormat', op: 'exists' },
        severity: 'error', message: 'HDR : primaires Rec. 2020 déclarées — sans cette étiquette, YouTube ne traite pas la vidéo en HDR.' },
      { id: 'hdr-transfert', track: 'video', quantifier: 'all', field: 'transferCharacteristics', op: 'inList', value: ['PQ', 'HLG'],
        when: { field: 'hdrFormat', op: 'exists' },
        severity: 'error', message: 'HDR : courbe de transfert PQ ou HLG déclarée.' },
      { id: 'short', track: 'video', quantifier: 'all', field: 'displayAspectRatio', op: 'gt', value: 1,
        when: [{ field: 'duration', op: 'lte', value: 180 }],
        severity: 'warning', message: 'Image verticale ou carrée de trois minutes au plus : YouTube classera la vidéo en Short.' }
    ].concat(ytBitrateRules())
  },
  {
    id: 'instagram-reels',
    name: 'Instagram — Reels (publication par l’API)',
    family: 'diffusion',
    version: '1.0.0', date: '2026-09', statut: 'maintenu',
    description: 'Exigences de l’API de publication de Meta pour un Reel : ce sont des conditions d’acceptation du fichier, pas des conseils. Elles valent pour la publication programmatique ; Meta ne publie pas les limites de l’envoi depuis l’application mobile.',
    notChecked: [
      'Le rapport d’image conseillé (9:16) : seule la fourchette 0,01:1 à 10:1 est une exigence.',
      'Le GOP fermé, que le modèle ne lit pas.',
      'Le niveau sonore, que Meta ne publie pas.'
    ],
    sources: [
      { title: 'Meta — Instagram Platform, IG User Media reference, « Reels specifications »', url: 'https://developers.facebook.com/docs/instagram-platform/instagram-graph-api/reference/ig-user/media/', consulted: TODAY }
    ],
    appliesTo: { requireTracks: { video: 1 } },
    rules: [
      { id: 'conteneur', track: 'container', field: 'formatRaw', op: 'inList', value: ['MPEG-4', 'QuickTime'],
        severity: 'error', message: 'Conteneur MP4 ou MOV.' },
      { id: 'moov', track: 'container', field: 'moovPosition', origin: 'measured', op: 'eq', value: 'before-mdat',
        severity: 'error', message: 'Index (moov) en tête du fichier.' },
      { id: 'vcodec', track: 'video', quantifier: 'all', field: 'formatRaw', op: 'inList', value: ['AVC', 'HEVC'],
        severity: 'error', message: 'Vidéo H.264 ou HEVC.' },
      { id: 'progressif', track: 'video', quantifier: 'all', field: 'scanType', op: 'eq', value: 'progressive',
        severity: 'error', message: 'Balayage progressif.' },
      { id: 'chroma', track: 'video', quantifier: 'all', field: 'chromaSubsampling', op: 'eq', value: '4:2:0',
        severity: 'error', message: 'Sous-échantillonnage chroma 4:2:0.' },
      { id: 'cadence', track: 'video', quantifier: 'all', field: 'frameRate', op: 'range', value: { min: 23, max: 60 },
        severity: 'error', message: 'Cadence comprise entre 23 et 60 images par seconde.' },
      { id: 'largeur', track: 'video', quantifier: 'all', field: 'width', op: 'lte', value: 1920,
        severity: 'error', message: 'Largeur de 1920 pixels au plus.' },
      { id: 'debit', track: 'video', quantifier: 'all', field: 'bitRate', op: 'lte', value: 25000000,
        severity: 'error', message: 'Débit vidéo de 25 Mb/s au plus.' },
      { id: 'duree', track: 'container', field: 'duration', op: 'range', value: { min: 3, max: 900 },
        severity: 'error', message: 'Durée comprise entre 3 secondes et 15 minutes.' },
      { id: 'poids', track: 'container', field: 'fileSize', op: 'lte', value: 314572800,
        severity: 'error', message: 'Fichier de 300 Mo au plus.' },
      { id: 'acodec', track: 'audio', quantifier: 'all', field: 'formatRaw', op: 'eq', value: 'AAC',
        severity: 'error', message: 'Audio AAC.' },
      { id: 'freq', track: 'audio', quantifier: 'all', field: 'samplingRate', op: 'lte', value: 48000,
        severity: 'error', message: 'Fréquence d’échantillonnage de 48 kHz au plus.' },
      { id: 'canaux', track: 'audio', quantifier: 'all', field: 'channels', op: 'inList', value: [1, 2],
        severity: 'error', message: 'Audio mono ou stéréo.' }
    ]
  },
  {
    id: 'tiktok-publication',
    name: 'TikTok — publication par l’API',
    family: 'diffusion',
    version: '1.0.0', date: '2026-09', statut: 'maintenu',
    description: 'Exigences du guide de transfert de la Content Posting API de TikTok (mis à jour le 4 août 2026). TikTok ne publie ni débit, ni rapport d’image, ni format audio pour la publication organique : ce profil ne contrôle donc que ce qui est documenté. Les spécifications publicitaires, différentes, ne sont pas reprises ici.',
    notChecked: [
      'Le format audio, la fréquence d’échantillonnage et les canaux : TikTok ne les publie pas.',
      'Le débit vidéo et le rapport d’image, absents de la documentation organique.',
      'La position de l’index (moov), non mentionnée par TikTok.'
    ],
    sources: [
      { title: 'TikTok for Developers — Content Posting API, Media Transfer Guide (mis à jour le 4 août 2026)', url: 'https://developers.tiktok.com/doc/content-posting-api-media-transfer-guide', consulted: TODAY }
    ],
    appliesTo: { requireTracks: { video: 1 } },
    rules: [
      { id: 'conteneur', track: 'container', field: 'formatRaw', op: 'inList', value: ['MPEG-4', 'WebM', 'QuickTime'],
        severity: 'error', message: 'Conteneur MP4 (recommandé), WebM ou MOV.' },
      { id: 'vcodec', track: 'video', quantifier: 'all', field: 'formatRaw', op: 'inList', value: ['AVC', 'HEVC', 'VP8', 'VP9'],
        severity: 'error', message: 'Vidéo H.264 (recommandé), H.265, VP8 ou VP9.' },
      { id: 'cadence', track: 'video', quantifier: 'all', field: 'frameRate', op: 'range', value: { min: 23, max: 60 },
        severity: 'error', message: 'Cadence comprise entre 23 et 60 images par seconde.' },
      { id: 'largeur', track: 'video', quantifier: 'all', field: 'width', op: 'range', value: { min: 360, max: 4096 },
        severity: 'error', message: 'Largeur comprise entre 360 et 4096 pixels.' },
      { id: 'hauteur', track: 'video', quantifier: 'all', field: 'height', op: 'range', value: { min: 360, max: 4096 },
        severity: 'error', message: 'Hauteur comprise entre 360 et 4096 pixels.' },
      { id: 'poids', track: 'container', field: 'fileSize', op: 'lte', value: 4294967296,
        severity: 'error', message: 'Fichier de 4 Go au plus.' },
      { id: 'duree', track: 'container', field: 'duration', op: 'lte', value: 600,
        severity: 'error', message: 'Durée de 10 minutes au plus par l’API.' }
    ]
  },
  {
    id: 'podcast-ecoute',
    name: 'Podcast — niveau d’écoute',
    family: 'production',
    version: '1.0.0', date: '2026-09', statut: 'maintenu',
    description: 'Apple Podcasts conseille de préparer le son autour de −16 LKFS, à un décibel près, avec une crête vraie qui ne dépasse pas −1 dBFS, faute de quoi la plateforme ajuste le volume elle-même. Les valeurs se mesurent selon ITU-R BS.1770 : lancez « Mesurer le niveau sonore » pour que ce profil se prononce.',
    notChecked: [
      'La plage de loudness : aucune source officielle n’en fixe de limite.',
      'Le format et le débit du fichier, qu’Apple traite à part de ces valeurs.'
    ],
    sources: [
      { title: 'Apple Podcasts for Creators — Audio requirements (sans date affichée)', url: 'https://podcasters.apple.com/support/893-audio-requirements', consulted: TODAY },
      { title: 'UIT-R BS.1770-5 — Algorithms to measure audio programme loudness and true-peak audio level (novembre 2023)', url: 'https://www.itu.int/rec/R-REC-BS.1770', consulted: TODAY }
    ],
    appliesTo: { requireTracks: { audio: 1 } },
    rules: [
      { id: 'niveau', track: 'audio', quantifier: 'all', field: 'loudnessIntegrated', origin: 'measured',
        op: 'tolerance', value: { target: -16, tol: 1 },
        severity: 'warning', message: 'Niveau intégré autour de −16 LKFS, à 1 dB près.' },
      { id: 'crete', track: 'audio', quantifier: 'all', field: 'truePeak', origin: 'measured', op: 'lte', value: -1,
        severity: 'warning', message: 'Crête vraie au plus à −1 dBFS.' }
    ]
  },
  {
    id: 'diffusion-ebu-r128',
    name: 'Diffusion télé et radio (EBU R 128)',
    family: 'diffusion',
    version: '1.0.0', date: '2026-09', statut: 'maintenu',
    description: 'La recommandation européenne de diffusion, dans sa version 5 de novembre 2023 : le niveau du programme « shall be normalised to a Target Level of −23.0 LUFS », avec une tolérance d’un décibel réservée aux cas où la cible n’est pas atteignable, par exemple en direct ; et la crête vraie « shall not exceed −1 dBTP » en production. Les valeurs se mesurent selon ITU-R BS.1770.',
    notChecked: [
      'La plage de loudness : la recommandation ne fixe aucune limite, et les guides de production disent explicitement de ne pas en faire un critère de livraison.',
      'Le niveau d’entrée d’un encodeur à perte, que la recommandation technique 3344 abaisse à −2 dBTP.'
    ],
    sources: [
      { title: 'EBU R 128 v5 — Loudness normalisation and permitted maximum level of audio signals (novembre 2023)', url: 'https://tech.ebu.ch/publications/r128/', consulted: TODAY },
      { title: 'EBU Tech 3343 v4 — Guidelines for Production (novembre 2023)', url: 'https://tech.ebu.ch/publications/tech3343/', consulted: TODAY }
    ],
    appliesTo: { requireTracks: { audio: 1 } },
    rules: [
      { id: 'niveau', track: 'audio', quantifier: 'all', field: 'loudnessIntegrated', origin: 'measured',
        op: 'tolerance', value: { target: -23, tol: 1 },
        severity: 'error', message: 'Niveau intégré de −23,0 LUFS, tolérance d’un décibel.' },
      { id: 'crete', track: 'audio', quantifier: 'all', field: 'truePeak', origin: 'measured', op: 'lte', value: -1,
        severity: 'error', message: 'Crête vraie au plus à −1 dBTP.' }
    ]
  },
  {
    id: 'archivage-video',
    name: 'Archivage vidéo — FFV1 dans Matroska',
    family: 'archivage',
    version: '1.0.0', date: '2026-09', statut: 'maintenu',
    description: 'La Library of Congress classe les formats vidéo par ordre de préférence pour la conservation. FFV1 version 3 dans un conteneur Matroska arrive au deuxième rang, derrière l’IMF/MXF réservé aux livraisons professionnelles ; c’est la combinaison retenue par la plupart des archives pour la numérisation de bandes. Un MP4 n’est accepté que comme copie de consultation, jamais comme master.',
    notChecked: [
      'La version de FFV1 : seule la version 3 est recommandée, mais le modèle ne lit pas encore ce numéro.',
      'La somme de contrôle par tranche de FFV1, présente seulement si le fichier a été encodé avec cette option.',
      'Le CRC-32 des éléments Matroska, recommandé par la RFC 9559 mais non exigé.',
      'L’absence de mesures techniques de protection (DRM), seule exigence formelle de la Library of Congress.'
    ],
    sources: [
      { title: 'Library of Congress — Recommended Formats Statement 2025-2026, « Moving Image Works », p. 20', url: 'https://www.loc.gov/preservation/resources/rfs/RFS%202025-2026.pdf', consulted: TODAY },
      { title: 'Library of Congress — Sustainability of Digital Formats, FFV1 in Matroska (mise à jour du 9 mai 2024)', url: 'https://www.loc.gov/preservation/digital/formats/fdd/fdd000343.shtml', consulted: TODAY },
      { title: 'IETF — RFC 9043, FFV1 Video Coding Format Versions 0, 1, and 3 (août 2021)', url: 'https://www.rfc-editor.org/rfc/rfc9043.html', consulted: TODAY }
    ],
    appliesTo: { requireTracks: { video: 1 } },
    rules: [
      { id: 'conteneur', track: 'container', field: 'formatRaw', op: 'eq', value: 'Matroska',
        severity: 'error', message: 'Conteneur Matroska (.mkv).' },
      { id: 'vcodec', track: 'video', quantifier: 'all', field: 'formatRaw', op: 'eq', value: 'FFV1',
        severity: 'error', message: 'Vidéo en FFV1, codec sans perte recommandé pour la conservation.' },
      { id: 'acodec', track: 'audio', quantifier: 'all', field: 'formatRaw', op: 'inList', value: ['PCM', 'FLAC'],
        severity: 'warning', message: 'Audio sans perte (PCM ou FLAC) : un codage perceptuel perd définitivement une partie du signal.' }
    ]
  },
  {
    id: 'archivage-audio',
    name: 'Archivage audio — master de conservation',
    family: 'archivage',
    version: '1.0.0', date: '2026-09', statut: 'maintenu',
    description: 'Master sonore destiné à être conservé, pas diffusé. L’IASA fixe deux planchers pour la numérisation : 48 kHz et 24 bits au minimum, et interdit les codecs à codage perceptuel, qui font perdre définitivement une partie du signal. La Library of Congress préfère le WAVE avec métadonnées embarquées (Broadcast WAVE) en PCM ; elle place la haute résolution (96 kHz / 24 bits) au-dessus, et la qualité CD (44,1 kHz / 16 bits) parmi les formats seulement acceptables.',
    notChecked: [
      'La présence du bloc de métadonnées « bext » qui distingue un Broadcast WAVE d’un WAVE ordinaire.',
      'La somme de contrôle MD5 des fichiers FLAC, que la norme autorise à laisser vide.',
      'Le rééchantillonnage : un fichier converti depuis une fréquence plus basse ne se distingue pas d’un original.',
      'Le niveau sonore, qui demande une mesure.'
    ],
    sources: [
      { title: 'IASA — TC 04, Guidelines on the Production and Preservation of Digital Audio Objects, 2e édition (2009), §2.2, 2.3, 2.7 et 2.8.2', url: 'https://www.iasa-web.org/tc04/key-digital-principles', consulted: TODAY },
      { title: 'Library of Congress — Recommended Formats Statement 2025-2026, « Audio Works », p. 22', url: 'https://www.loc.gov/preservation/resources/rfs/RFS%202025-2026.pdf', consulted: TODAY },
      { title: 'Library of Congress — Sustainability of Digital Formats, EBU Broadcast Wave Format (mise à jour du 23 avril 2024)', url: 'https://www.loc.gov/preservation/digital/formats/fdd/fdd000356.shtml', consulted: TODAY }
    ],
    appliesTo: { requireTracks: { audio: 1 } },
    rules: [
      { id: 'sans-perte', track: 'audio', quantifier: 'all', field: 'formatRaw', op: 'inList', value: ['PCM', 'FLAC', 'ALAC'],
        severity: 'error', message: 'Codec sans perte : l’IASA écarte tout codage perceptuel pour un master de conservation.' },
      { id: 'freq', track: 'audio', quantifier: 'all', field: 'samplingRate', op: 'gte', value: 48000,
        severity: 'error', message: 'Fréquence d’échantillonnage de 48 kHz au minimum (IASA) ; 96 kHz pour la haute résolution.' },
      { id: 'profondeur', track: 'audio', quantifier: 'all', field: 'bitDepth', op: 'gte', value: 24,
        severity: 'error', message: 'Profondeur de 24 bits au minimum (IASA).' },
      { id: 'conteneur', track: 'container', field: 'formatRaw', op: 'inList', value: ['Wave', 'FLAC'],
        severity: 'warning', message: 'WAVE avec métadonnées embarquées (Broadcast WAVE) préféré par la Library of Congress ; FLAC relève de la compression sans perte, classée acceptable.' }
    ]
  }
];

var api = { PROFILES: PROFILES };
globalThis.MediaProfiles = api;
export default api;
